import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FavoriteBorder,
  LocationOn,
  Map as MapIcon,
} from '@mui/icons-material';
import { catService, getImageUrl, hasValidLocation } from '../../services/api';

interface Cat {
  _id: string;
  name: string;
  breed: string;
  notes: string;
  imageUrl: string;
  location: { latitude: number; longitude: number };
  likes_pats: boolean;
  uploadedBy: { username: string; profilePicture: string };
  createdAt: string;
}

const CatList: React.FC = () => {
  const [cats, setCats] = useState<Cat[]>([]);
  const [filteredCats, setFilteredCats] = useState<Cat[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const data = await catService.getAll();
        setCats(data);
        setFilteredCats(data);
      } catch (error) {
        console.error('Failed to fetch cats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = cats.filter(
      (cat) =>
        cat.name.toLowerCase().includes(query) ||
        cat.breed?.toLowerCase().includes(query) ||
        cat.notes?.toLowerCase().includes(query)
    );
    setFilteredCats(filtered);
  }, [searchQuery, cats]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        py: 4,
        background: 'linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              mb: 1,
              background: 'linear-gradient(135deg, #f1f5f9 0%, #94a3b8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Discover Cats
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Browse our community of adorable felines
          </Typography>
        </Box>

        <TextField
          fullWidth
          placeholder="Search cats by name, breed, or notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 4,
            maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255,255,255,0.03)',
            },
          }}
        />

        {filteredCats.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <Typography variant="h6">No cats found</Typography>
            <Typography variant="body2">Try a different search term</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredCats.map((cat) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat._id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(124, 58, 237, 0.2)',
                    },
                  }}
                  onClick={() => navigate(`/cats/${cat._id}`)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={getImageUrl(cat.imageUrl)}
                      alt={cat.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    {hasValidLocation(cat) && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/?catId=${cat._id}`);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          backdropFilter: 'blur(4px)',
                          '&:hover': {
                            bgcolor: 'primary.main',
                          },
                        }}
                      >
                        <MapIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {cat.name}
                    </Typography>
                    {cat.breed && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        {cat.breed}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {cat.likes_pats && (
                        <Chip
                          icon={<FavoriteBorder sx={{ fontSize: 14 }} />}
                          label="Likes Pats"
                          size="small"
                          sx={{
                            bgcolor: 'rgba(34, 197, 94, 0.1)',
                            color: 'success.main',
                            borderColor: 'success.main',
                          }}
                          variant="outlined"
                        />
                      )}
                      {hasValidLocation(cat) && (
                        <Chip
                          icon={<LocationOn sx={{ fontSize: 14 }} />}
                          label="On Map"
                          size="small"
                          sx={{
                            bgcolor: 'rgba(6, 182, 212, 0.1)',
                            color: 'secondary.main',
                            borderColor: 'secondary.main',
                          }}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CatList;
